/* eslint-disable react/jsx-props-no-spreading */

import PropTypes from 'prop-types';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  graphql,
  usePreloadedQuery,
} from 'react-relay';

import AdminPortfolioUserMemberShow from 'src/components/Admin/AdminPortfolioUserMember/AdminPortfolioUserMemberShow';
import AdminShowPlaceholder from 'src/components/Admin/AdminShowPlaceholder';
import PlaceholderErrorBoundary from 'src/components/ErrorBoundary/PlaceholderErrorBoundary';
import Loading from 'src/components/Loading';

import RouteErrorThrower from './helpers/RouteErrorThrower';
import getShowRouteKey from './helpers/getShowRouteKey';
import useRouteQueryRetry from './helpers/useRouteQueryRetry';

const messages = defineMessages({
  initialLoadError: {
    id: 'admin.admin_portfolio_user_member.admin_portfolio_user_member_show.page.initial_load_error.message',
    defaultMessage: 'An error occurred while loading this portfolio user member. Please try again.',
  },
  notFoundTitle: {
    id: 'admin.admin_portfolio_user_member.admin_portfolio_user_member_show.page.not_found.title',
    defaultMessage: 'Portfolio user member not found',
  },
});

export const adminPortfolioUserMemberShowRouteQuery = graphql`
  query AdminPortfolioUserMemberShowRoute_Query($uuid: ID!) {
    portfolioUserMember(uuid: $uuid) {
      ...AdminPortfolioUserMemberShow_portfolioUserMember
    }
  }
`;

/**
 * Renders `AdminPortfolioUserMemberShow` from a manually retried Relay query reference.
 * @param {object} props - Component props.
 * @param {object} props.routeProps - Original found-relay route props.
 * @param {object} props.queryRef - Relay preloaded query reference created by `loadQuery`.
 * @returns {React.ReactNode} admin portfolio user member show route content.
 */
function AdminPortfolioUserMemberShowPreloadedQueryRenderer({ routeProps, queryRef }) {
  const queryData = usePreloadedQuery(adminPortfolioUserMemberShowRouteQuery, queryRef);

  return <AdminPortfolioUserMemberShow {...routeProps} {...queryData} />;
}

AdminPortfolioUserMemberShowPreloadedQueryRenderer.propTypes = {
  routeProps: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  queryRef: PropTypes.shape({
    dispose: PropTypes.func,
  }).isRequired,
};

/**
 * Hook-safe renderer for the `AdminPortfolioUserMemberShow` route.
 * Keeps the initial found-relay result for first paint, then switches to a
 * `useQueryLoader`-managed query reference when the user retries after an
 * error so Relay can suspend back to the loading state. Nested inside the
 * `AdminLayout` boundary, which covers admin-shell errors.
 * @param {object} props - Component props.
 * @param {object} props.routeProps - Original found-relay route props.
 * @param {Error|null} [props.routeError] - Network-level error from found-relay.
 * @returns {React.ReactNode} admin portfolio user member show route content.
 */
function AdminPortfolioUserMemberShowRouteRenderer({ routeProps, routeError = null }) {
  const intl = useIntl();
  const { queryRef, handleRetry, retryDisabled } = useRouteQueryRetry(
    adminPortfolioUserMemberShowRouteQuery,
    routeProps,
    { logLabel: 'AdminPortfolioUserMemberShowRoute' },
  );

  return (
    <PlaceholderErrorBoundary
      bodyText={intl.formatMessage(messages.initialLoadError)}
      notFound={{ title: intl.formatMessage(messages.notFoundTitle), backPath: '/admin/portfolios' }}
      placeholderComponent={AdminShowPlaceholder}
      onRetryFunc={handleRetry}
      retryDisabled={retryDisabled}
    >
      <React.Suspense fallback={<Loading />}>
        {routeError && !queryRef ? (
          <RouteErrorThrower error={routeError} />
        ) : null}
        {queryRef ? (
          <AdminPortfolioUserMemberShowPreloadedQueryRenderer
            routeProps={routeProps}
            queryRef={queryRef}
          />
        ) : (
          <AdminPortfolioUserMemberShow {...routeProps} />
        )}
      </React.Suspense>
    </PlaceholderErrorBoundary>
  );
}

AdminPortfolioUserMemberShowRouteRenderer.propTypes = {
  routeProps: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  routeError: PropTypes.instanceOf(Error),
};

/**
 * Renders the AdminPortfolioUserMemberShow route state supplied by found-relay.
 * A network error is forwarded to the renderer's `RouteErrorThrower` bridge so
 * the local error boundary can catch it. The renderer is keyed with
 * `getShowRouteKey` (uuid and non-locale query params), so any navigation
 * remounts it and drops a retried query reference that would otherwise hide a
 * later route error. Authentication is resolved by the owning `AdminLayout`
 * route, not here.
 * @param {object} readyState - Found-relay route render state.
 * @param {Error|null} readyState.error - Route query error.
 * @param {object} readyState.match - Found route match, available while props are pending.
 * @param {object|null} readyState.props - Resolved route query props.
 * @returns {React.ReactNode} AdminPortfolioUserMemberShow route content or loading state.
 */
export function adminPortfolioUserMemberShowRoute({ error = null, match, props = null }) {
  if (error) {
    const routeProps = props || { match, router: match.router };
    return (
      <AdminPortfolioUserMemberShowRouteRenderer
        key={getShowRouteKey(routeProps)}
        routeError={error}
        routeProps={routeProps}
      />
    );
  }

  if (!props) {
    return <Loading />;
  }

  return (
    <AdminPortfolioUserMemberShowRouteRenderer
      key={getShowRouteKey(props)}
      routeProps={props}
    />
  );
}

export default AdminPortfolioUserMemberShowRouteRenderer;
